import { useState, useEffect, useCallback, useRef } from 'react';
import { CopyOutlined, CheckCircleFilled, LoadingOutlined } from '@ant-design/icons';
import { Tabs } from 'antd';
import {
  AGENT_PROVIDERS,
  AGENT_PROVIDER_LABELS,
  type AgentProvider,
  type Platform,
  providerHasPlatformDifference,
  buildAgentDisconnectCommandParts,
  resolveAgentProvider,
} from './AgentConnectResult';
import { useDismissibleLayer } from '../hooks/useDismiss';
import { usePresence } from '../hooks/usePresence';
import * as api from '../lib/api';
import type { HostedAgentPlacement } from '../lib/api';
import { copyToClipboard } from '../lib/clipboard';

const DISCONNECT_COPY: Record<AgentProvider, {
  stepLabel: string;
  doneLabel: (name: string) => string;
  instruction: (name: string) => string;
  skipHint: string;
}> = {
  openclaw: {
    stepLabel: 'Remove the Borgee plugin',
    doneLabel: (name) => `${name} plugin removed`,
    instruction: (name) => `Remove the Borgee plugin from ${name}:`,
    skipHint: 'If you skip this step, the plugin will fail to connect after deletion.',
  },
  claude: {
    stepLabel: 'Stop the agent process',
    doneLabel: (name) => `${name} disconnected`,
    instruction: (name) => `Stop ${name} on your machine:`,
    skipHint: "If you can't stop the process, you can skip this step — the agent will be disconnected automatically after deletion.",
  },
  copilot: {
    stepLabel: 'Stop the agent process',
    doneLabel: (name) => `${name} disconnected`,
    instruction: (name) => `Stop ${name} on your machine:`,
    skipHint: "If you can't stop the process, you can skip this step — the agent will be disconnected automatically after deletion.",
  },
  codex: {
    stepLabel: 'Stop the agent process',
    doneLabel: (name) => `${name} disconnected`,
    instruction: (name) => `Stop ${name} on your machine:`,
    skipHint: "If you can't stop the process, you can skip this step — the agent will be disconnected automatically after deletion.",
  },
  dsh: {
    stepLabel: 'Stop DSH Web',
    doneLabel: (name) => `${name} disconnected`,
    instruction: () => 'Stop the DSH Web process that hosts the Borgee connector:',
    skipHint: "If you can't stop DSH Web, you can skip this step — the agent will be disconnected automatically after deletion.",
  },
  praestoclaw: {
    stepLabel: 'Stop the agent process',
    doneLabel: (name) => `${name} disconnected`,
    instruction: (name) => `Stop ${name} on your machine:`,
    skipHint: "If you can't stop the process, you can skip this step — the agent will be disconnected automatically after deletion.",
  },
  hermes: {
    stepLabel: 'Stop the bridge process',
    doneLabel: () => `borgee-hermes-plugin disconnected`,
    instruction: () => `Stop the borgee-hermes-plugin on your machine:`,
    skipHint: "If you can't stop the process, you can skip this step — the agent will be disconnected automatically after deletion.",
  },
  kosmos: {
    stepLabel: 'Disconnect the Borgee remote channel',
    doneLabel: (name) => `${name} disconnected`,
    instruction: () => 'In Kosmos Desktop, open Settings → Remote Channel → Borgee and click Disconnect:',
    skipHint: "If you can't reach Kosmos now, you can skip this step — the agent will be disconnected automatically after deletion.",
  },
};

interface Props {
  agentId: string;
  agentName: string;
  agentState?: string;
  lastConnectedPluginId?: string;
  hostedPlacement?: HostedAgentPlacement | null;
  hostedComputerName?: string | null;
  onClose: () => void;
  onDeleted: () => void;
}

const BADGE_DONE: React.CSSProperties = { width: 18, height: 18, borderRadius: '50%', background: 'var(--color-success, #22c55e)', color: '#fff', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 };
const BADGE_ACTIVE: React.CSSProperties = { width: 18, height: 18, borderRadius: '50%', border: '2px solid var(--accent-primary, #5865f2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1, fontSize: 11, color: 'var(--accent-primary, #5865f2)' };
const BADGE_FUTURE: React.CSSProperties = { width: 18, height: 18, borderRadius: '50%', border: '2px solid var(--text-tertiary, #ccc)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1, fontSize: 11, color: 'var(--text-tertiary, #999)' };

/**
 * One screen of the decommission modal: the scrim covering the app, and the
 * card drawn in it.
 *
 * The scrim is a dismissible layer, and it is the scrim rather than the card
 * that is the layer's box. That is what makes the press which closes this modal
 * belong to it: a press on the scrim lands inside the layer, so nothing the
 * modal was raised over — a floating thread panel, a menu — is dismissed by the
 * same press. Escape stops here for the same reason.
 *
 * `busy` keeps the modal on the stack and stops both gestures acting: the work
 * they would be cancelling has already gone out, and it is not the modal's to
 * call back. Unmounting mid-request would take the failure report with it —
 * the delete's error has nowhere to land — and leave the channel-removal loop,
 * which is a plain `for` over the agent's channels rather than anything tied to
 * this component, still removing the agent from the rest of them.
 */
export function DecommissionModalSurface({ maxWidth, busy = false, onClose, children }: { maxWidth: number; busy?: boolean; onClose: () => void; children: React.ReactNode }) {
  const scrimRef = useRef<HTMLDivElement>(null);

  useDismissibleLayer({ active: true, within: scrimRef, onEscape: busy ? undefined : onClose });

  return (
    <div ref={scrimRef} className="admin-modal" onClick={busy ? undefined : onClose}>
      <div className="admin-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth }}>
        {children}
      </div>
    </div>
  );
}

export default function DecommissionAgentModal({
  agentId,
  agentName,
  agentState,
  lastConnectedPluginId,
  hostedPlacement: hostedPlacementProp,
  hostedComputerName: hostedComputerNameProp,
  onClose,
  onDeleted,
}: Props) {
  const [step, setStep] = useState<'confirm' | 'provider' | 'flow'>('confirm');
  const [hostedPlacementState, setHostedPlacementState] = useState<HostedAgentPlacement | null>(hostedPlacementProp ?? null);
  const [hostedComputerNameState, setHostedComputerNameState] = useState<string | null>(hostedComputerNameProp ?? null);
  const hostedPlacement = hostedPlacementProp ?? hostedPlacementState;
  const hostedComputerName = hostedComputerNameProp ?? hostedComputerNameState;
  const isHostedAgent = Boolean(hostedPlacement);
  const [selectedProvider, setSelectedProvider] = useState<AgentProvider>(() => hostedPlacement?.provider ?? resolveAgentProvider(agentId, lastConnectedPluginId));
  const hostedMachineLabel = hostedComputerName ?? hostedPlacement?.node_id ?? 'this computer';
  const continueStep = () => setStep(isHostedAgent ? 'flow' : 'provider');

  useEffect(() => {
    if (hostedPlacementProp) {
      setHostedPlacementState(hostedPlacementProp);
      setHostedComputerNameState(hostedComputerNameProp ?? null);
      return;
    }
    let cancelled = false;
    const loadHostedDetails = async () => {
      try {
        const { blob } = await api.fetchAgentConfig(agentId);
        if (cancelled) return;
        const hosted = blob.hosted ?? null;
        setHostedPlacementState(hosted);
        if (!hosted) {
          setHostedComputerNameState(null);
          return;
        }
        const nodes = await api.fetchRemoteNodes();
        if (cancelled) return;
        setHostedComputerNameState(
          nodes.find((node) => node.id === hosted.node_id)?.machine_name ?? hosted.node_id,
        );
      } catch {
        if (!cancelled) {
          setHostedPlacementState(null);
          setHostedComputerNameState(null);
        }
      }
    };
    void loadHostedDetails();
    return () => {
      cancelled = true;
    };
  }, [agentId, hostedComputerNameProp, hostedPlacementProp]);

  useEffect(() => {
    if (hostedPlacement?.provider) {
      setSelectedProvider(hostedPlacement.provider);
    }
  }, [hostedPlacement]);

  if (step === 'confirm') {
    return (
      <DecommissionModalSurface maxWidth={420} onClose={onClose}>
        <h3>{isHostedAgent ? `Delete hosted agent ${agentName}?` : `Delete ${agentName}?`}</h3>
        <p style={{ fontSize: 13, margin: '16px 0', color: 'var(--text-secondary)' }}>
          {isHostedAgent ? (
            <>
              This will remove <b>{agentName}</b> from all channels, stop <b>{hostedMachineLabel}</b> from hosting it, and permanently delete the agent. This will not delete the computer or affect any other hosted agents on it.
            </>
          ) : (
            <>
              This will remove <b>{agentName}</b> from all channels, disconnect it, and permanently delete the agent. This cannot be undone.
            </>
          )}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-sm" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-sm"
            style={{ background: 'var(--text-danger, #d32f2f)', color: '#fff', borderColor: 'var(--text-danger, #d32f2f)' }}
            onClick={continueStep}
          >
            Continue
          </button>
        </div>
      </DecommissionModalSurface>
    );
  }

  if (step === 'provider') {
    const cardStyle = (selected: boolean): React.CSSProperties => ({
      padding: 16,
      borderRadius: 8,
      border: `2px solid ${selected ? 'var(--accent-primary, #5865f2)' : 'var(--border-color, #e0e0e0)'}`,
      cursor: 'pointer',
      background: selected ? 'var(--bg-hover, #f0f0ff)' : 'var(--bg-primary, #fff)',
      transition: 'all 0.15s',
    });
    return (
      <DecommissionModalSurface maxWidth={420} onClose={onClose}>
        <h3>How is {agentName} connected?</h3>
        {/* Seeded from resolveAgentProvider, which can answer with any provider,
            so the cards are the provider list itself — a provider with no card
            here leaves the owner no way to answer truthfully. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
          {AGENT_PROVIDERS.map(provider => (
            <div key={provider} style={cardStyle(selectedProvider === provider)} onClick={() => setSelectedProvider(provider)}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{AGENT_PROVIDER_LABELS[provider]}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button className="btn btn-sm" onClick={() => setStep('confirm')}>← Back</button>
          <button className="btn btn-sm btn-primary" onClick={() => setStep('flow')}>
            Next
          </button>
        </div>
      </DecommissionModalSurface>
    );
  }

  return (
    <DecommissionFlow
      agentId={agentId}
      agentName={agentName}
      agentState={agentState}
      provider={selectedProvider}
      hostedPlacement={hostedPlacement}
      hostedComputerName={hostedComputerName}
      onClose={onClose}
      onDeleted={onDeleted}
    />
  );
}

function DecommissionFlow({
  agentId,
  agentName,
  agentState,
  provider,
  hostedPlacement,
  hostedComputerName,
  onClose,
  onDeleted,
}: Omit<Props, 'lastConnectedPluginId'> & { provider: AgentProvider }) {
  const [channels, setChannels] = useState<{ id: string; name: string }[]>([]);
  const [removedChannels, setRemovedChannels] = useState<Set<string>>(new Set());
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [removing, setRemoving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hostedDeleteRequested, setHostedDeleteRequested] = useState(false);
  const [hostedDeleteSettled, setHostedDeleteSettled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [platform, setPlatform] = useState<Platform>('linux');
  const removingRef = useRef(false);

  const presence = usePresence(agentId);
  const effectiveState = presence?.state ?? agentState ?? 'offline';
  const isOffline = effectiveState === 'offline';

  const allRemoved = channels.length === 0 || removedChannels.size === channels.length;
  const step1Done = allRemoved && !channelsLoading;
  const isHostedAgent = Boolean(hostedPlacement);
  const step2Done = isHostedAgent ? hostedDeleteRequested : step1Done && isOffline;
  const step3Done = isHostedAgent ? hostedDeleteSettled : false;

  const hasPlatformDiff = providerHasPlatformDifference(provider);
  const disconnectParts = buildAgentDisconnectCommandParts(provider, agentName, platform);
  const copy = DISCONNECT_COPY[provider];
  const hostedMachineLabel = hostedComputerName ?? hostedPlacement?.node_id ?? 'this computer';
  const step2Label = isHostedAgent ? 'Delete hosted agent' : copy.stepLabel;
  const step2DoneLabel = isHostedAgent ? `${agentName} deleted` : copy.doneLabel(agentName);
  const waitingCopy = isHostedAgent
    ? `Refreshing hosted agents on ${hostedMachineLabel}...`
    : `Waiting for ${agentName} to go offline...`;
  const hostedDeletePending = isHostedAgent && hostedDeleteRequested && !hostedDeleteSettled;

  useEffect(() => {
    api.getAgentChannels(agentId).then(chs => {
      setChannels(chs);
      setChannelsLoading(false);
    }).catch(() => setChannelsLoading(false));
  }, [agentId]);

  useEffect(() => {
    if (!isHostedAgent || !hostedPlacement || !hostedDeleteRequested || hostedDeleteSettled) return;
    let cancelled = false;
    let retryTimer: number | undefined;

    const pollHostedAgents = async () => {
      try {
        const agents = await api.fetchRemoteNodeHostedAgents(hostedPlacement.node_id);
        if (cancelled) return;
        if (!agents.some((agent) => agent.id === agentId)) {
          setHostedDeleteSettled(true);
          return;
        }
      } catch {
        if (cancelled) return;
      }
      retryTimer = window.setTimeout(() => {
        void pollHostedAgents();
      }, 1000);
    };

    void pollHostedAgents();
    return () => {
      cancelled = true;
      if (retryTimer !== undefined) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [agentId, hostedDeleteRequested, hostedDeleteSettled, hostedPlacement, isHostedAgent]);

  const handleStartRemoval = useCallback(async () => {
    if (removingRef.current) return;
    removingRef.current = true;
    setRemoving(true);
    for (const ch of channels) {
      if (removedChannels.has(ch.id)) continue;
      try {
        await api.removeChannelMember(ch.id, agentId);
      } catch {
        // Some channels (e.g. #general) reject member removal — skip and
        // let handleDeleteAgent clean up via direct DB removal.
      }
      setRemovedChannels(prev => new Set(prev).add(ch.id));
    }
    setRemoving(false);
    removingRef.current = false;
  }, [agentId, channels, removedChannels]);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    setError(null);
    try {
      await api.deleteAgent(agentId);
      if (isHostedAgent) {
        setHostedDeleteRequested(true);
        setDeleting(false);
        return;
      }
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
      setDeleting(false);
    }
  }, [agentId, isHostedAgent, onDeleted]);

  const handleHostedDone = useCallback(() => {
    onDeleted();
  }, [onDeleted]);

  return (
    <DecommissionModalSurface maxWidth={520} busy={removing || deleting} onClose={onClose}>
      <h3>{isHostedAgent ? `Delete hosted agent ${agentName}` : `Decommission ${agentName}`}</h3>

      {error && <p style={{ color: 'var(--text-danger, #d32f2f)', fontSize: 13, margin: '12px 0' }}>{error}</p>}

      {/* Step 1: Remove from channels */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 16, marginBottom: 8 }}>
        {step1Done ? (
          <>
            <span style={BADGE_DONE}>&#10003;</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-success, #22c55e)' }}>Removed from all channels</span>
          </>
        ) : (
          <>
            <span style={BADGE_ACTIVE}>1</span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>Remove from channels</span>
            {!channelsLoading && channels.length > 0 && !removing && (
              <button
                className="btn btn-sm btn-primary"
                style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 10px' }}
                onClick={handleStartRemoval}
              >
                Start
              </button>
            )}
          </>
        )}
      </div>
      {!step1Done && (
        <div style={{ marginLeft: 28 }}>
          {channelsLoading ? (
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}><LoadingOutlined /> Loading...</span>
          ) : channels.length === 0 ? (
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No channels to remove from.</span>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 160, overflowY: 'auto', marginBottom: 8 }}>
              {channels.map(ch => {
                const done = removedChannels.has(ch.id);
                return (
                  <div key={ch.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    {done ? (
                      <CheckCircleFilled style={{ color: 'var(--color-success, #22c55e)', fontSize: 14 }} />
                    ) : removing ? (
                      <LoadingOutlined style={{ fontSize: 14, color: 'var(--text-secondary)' }} />
                    ) : (
                      <span style={{ width: 14, height: 14, display: 'inline-block' }} />
                    )}
                    <span style={{ color: done ? 'var(--text-tertiary, #999)' : undefined, textDecoration: done ? 'line-through' : undefined }}>
                      #{ch.name}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Step 2: Stop the agent process */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 12, marginBottom: 8 }}>
        {step2Done ? (
          <>
            <span style={BADGE_DONE}>&#10003;</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-success, #22c55e)' }}>{step2DoneLabel}</span>
          </>
        ) : isHostedAgent && step1Done ? (
          <>
            <span style={BADGE_ACTIVE}>2</span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>{step2Label}</span>
            <button
              className="btn btn-sm"
              style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 10px' }}
              disabled={deleting || hostedDeletePending}
              onClick={handleDelete}
            >
              {deleting ? 'Deleting...' : hostedDeletePending ? 'Refreshing...' : 'Delete'}
            </button>
          </>
        ) : step1Done ? (
          <>
            <span style={BADGE_ACTIVE}>2</span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>{step2Label}</span>
          </>
        ) : (
          <>
            <span style={BADGE_FUTURE}>2</span>
            <span style={{ fontSize: 13, color: 'var(--text-tertiary, #999)' }}>{step2Label}</span>
          </>
        )}
      </div>
      {step1Done && !step2Done && (
        <div style={{ marginLeft: 28 }}>
          {isHostedAgent ? (
            <>
              <p style={{ fontSize: 12, marginBottom: 8, color: 'var(--text-secondary)' }}>
                Delete <b>{agentName}</b> to revoke its credentials, disconnect it, and nudge <b>{hostedMachineLabel}</b> to remove the hosted runtime.
              </p>
              <p style={{ fontSize: 12, marginBottom: 8, color: 'var(--text-secondary)' }}>
                Confirm the delete here, then Borgee refreshes the hosted-agent list for <b>{hostedMachineLabel}</b> before moving this flow to Done.
              </p>
            </>
          ) : (
            <>
              <p style={{ fontSize: 12, marginBottom: 8, color: 'var(--text-secondary)' }}>
                {copy.instruction(agentName)}
              </p>
              {hasPlatformDiff && (
                <Tabs
                  activeKey={platform}
                  onChange={(key: string) => setPlatform(key as Platform)}
                  size="small"
                  style={{ marginBottom: 4 }}
                  items={[
                    { key: 'windows', label: 'Windows' },
                    { key: 'linux', label: 'Linux / macOS' },
                  ]}
                />
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                {disconnectParts.map((cmd, i) => (
                  <div key={i} style={{ position: 'relative', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: 6, padding: 10, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.5, fontFamily: 'monospace' }}>
                    {cmd}
                    <button
                      className="btn btn-sm"
                      style={{ position: 'absolute', top: 6, right: 6, fontSize: 11, padding: '2px 6px' }}
                      onClick={() => copyToClipboard(cmd.replace(/^#.*\n/gm, '').trim())}
                    >
                      <CopyOutlined />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
            <LoadingOutlined style={{ fontSize: 14 }} />
            <span>{hostedDeletePending ? waitingCopy : isHostedAgent ? `Ready to delete ${agentName} and stop hosting on ${hostedMachineLabel}.` : waitingCopy}</span>
          </div>
          <p style={{ fontSize: 11, color: 'var(--text-tertiary, #999)', marginTop: 8 }}>
            {isHostedAgent
              ? 'This will not delete the computer or affect other hosted agents on it.'
              : copy.skipHint}
          </p>
        </div>
      )}

      {isHostedAgent && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 12, marginBottom: 16 }}>
          {step3Done ? (
            <>
              <span style={BADGE_DONE}>&#10003;</span>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-success, #22c55e)' }}>Done</span>
            </>
          ) : step2Done ? (
            <>
              <span style={BADGE_ACTIVE}>3</span>
              <span style={{ fontSize: 13, fontWeight: 500 }}>Refreshing hosted status</span>
            </>
          ) : (
            <>
              <span style={BADGE_FUTURE}>3</span>
              <span style={{ fontSize: 13, color: 'var(--text-tertiary, #999)' }}>Done</span>
            </>
          )}
        </div>
      )}

      {!isHostedAgent && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 12, marginBottom: 16 }}>
          {step2Done ? (
            <>
              <span style={BADGE_ACTIVE}>3</span>
              <span style={{ fontSize: 13, fontWeight: 500 }}>Delete agent</span>
            </>
          ) : (
            <>
              <span style={BADGE_FUTURE}>3</span>
              <span style={{ fontSize: 13, color: 'var(--text-tertiary, #999)' }}>Delete agent</span>
            </>
          )}
        </div>
      )}

      <button
        className="btn btn-sm"
        style={{
          width: '100%',
          ...(isHostedAgent
            ? step3Done
              ? { background: 'var(--accent-primary, #5865f2)', color: '#fff', borderColor: 'var(--accent-primary, #5865f2)' }
              : { color: 'var(--text-secondary)' }
            : step2Done
              ? { background: 'var(--text-danger, #d32f2f)', color: '#fff', borderColor: 'var(--text-danger, #d32f2f)' }
              : { color: 'var(--text-secondary)' }),
        }}
        disabled={deleting || (isHostedAgent ? false : !step1Done) || hostedDeletePending}
        onClick={
          isHostedAgent
            ? step3Done
              ? handleHostedDone
              : onClose
            : step2Done
              ? handleDelete
              : step1Done
                ? handleDelete
                : onClose
        }
      >
        {isHostedAgent
          ? deleting
            ? 'Deleting Hosted Agent...'
            : step3Done
              ? 'Done'
              : hostedDeletePending
                ? 'Refreshing hosted status...'
                : step2Done
                  ? 'Waiting for hosted status...'
                  : 'I\'ll do this later'
          : deleting
            ? 'Deleting...'
            : step2Done
              ? 'Delete Agent'
              : step1Done
                ? 'Skip, delete anyway'
                : 'Skip, I\'ll do this later'}
      </button>
    </DecommissionModalSurface>
  );
}
